import React, { useState } from 'react';
import { PhoneIcon, MailIcon, MapPinIcon, MessageCircleIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { CONTACT } from '../data/copy';
import { useLanguage } from '../contexts/LanguageContext';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const initialForm = {
  name: '',
  phone: '',
  service: '',
  message: ''
};
export function ContactSection() {
  const {
    t
  } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState<Status>('idle');
  const update = (field: keyof typeof initialForm, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    window.setTimeout(() => {
      if (!form.name.trim() || !form.phone.trim()) {
        setStatus('error');
        return;
      }
      setStatus('success');
      setForm(initialForm);
    }, 900);
  };
  const inputClass = 'w-full rounded-md border border-neutral-300 bg-white px-4 py-3 text-[15px] text-neutral-900 placeholder:text-neutral-400 transition-colors duration-150 ease-out focus:border-rust focus:outline-none focus:ring-2 focus:ring-rust/25';
  return <section id="contact" className="w-full bg-sand py-20 lg:py-28" data-id="element-15" data-mp="ckzqzq1gzarl">
      <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-16" data-mp="ql0r339wue60">
        <div className="max-w-2xl" data-id="element-17" data-mp="qjfxbfvf6z97">
          <h2 className="font-display text-3xl font-black tracking-tight text-forest sm:text-4xl lg:text-5xl" data-id="element-18" data-mp="llxmfjxue9yk">
            {t.contact.title}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600" data-id="element-19" data-mp="109ir3iad8h8l">
            {t.contact.intro}
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-12" data-id="element-20" data-mp="vdtunntn7ggi">
          <div className="rounded-lg bg-forest p-8" data-id="element-21" data-mp="1995rc0rczh02">
            <h3 className="font-display text-xl font-bold text-white" data-id="element-22" data-mp="14dgn3k4e9bxf">
              {t.contact.panelTitle}
            </h3>
            <ul className="mt-7 space-y-6" data-id="element-23" data-mp="20u80d9lpddw">
              <li className="flex gap-4" data-id="element-24" data-mp="6xucwkupsa3f">
                <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-rust-light" aria-hidden="true" data-id="element-25" data-mp="6ylyr3yietgi" />
                <span data-id="element-26" data-mp="ay4kg1y5n8ke">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-white/55" data-id="element-27" data-mp="axl1qj0okg5b">
                    {t.contact.phoneLabel}
                  </span>
                  <a href={CONTACT.phoneHref} className="font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:text-rust-light" data-id="element-28" data-mp="koz6p6bei85u">
                    {CONTACT.phone}
                  </a>
                </span>
              </li>
              <li className="flex gap-4" data-id="element-29" data-mp="yn5ws0a3z3uy">
                <MailIcon className="mt-0.5 h-5 w-5 shrink-0 text-rust-light" aria-hidden="true" data-id="element-30" data-mp="2wfjlg8kw9sk9" />
                <span className="min-w-0" data-id="element-31" data-mp="30g3k5gl4948i">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-white/55" data-id="element-32" data-mp="30gwtmpbgm7uc">
                    {t.contact.emailLabel}
                  </span>
                  <a href={`mailto:${CONTACT.email}`} className="block break-words text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:text-rust-light" data-id="element-33" data-mp="2o5cjxs4awlc7">
                    {CONTACT.email}
                  </a>
                </span>
              </li>
              <li className="flex gap-4" data-id="element-34" data-mp="3btvqo37pcq26">
                <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-rust-light" aria-hidden="true" data-id="element-35" data-mp="36wde82uh7spf" />
                <span data-id="element-36" data-mp="32uihlbnhq3hz">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-white/55" data-id="element-37" data-mp="32tyvrjupyuyo">
                    {t.contact.locationLabel}
                  </span>
                  <span className="text-[15px] font-semibold text-white" data-id="element-38" data-mp="1rgeo06ktehdg">
                    {CONTACT.location}
                  </span>
                </span>
              </li>
            </ul>

            <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" className="mt-8 flex items-center justify-center gap-2.5 rounded-md bg-whatsapp px-6 py-3.5 text-center font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:bg-[#1DB456]" data-id="element-39" data-mp="3ad81bu3e4tc4">
              <MessageCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-40" data-mp="35ckap9j2qeif" />
              {t.contact.whatsappCta}
            </a>
          </div>

          <form onSubmit={handleSubmit} noValidate className="rounded-lg border border-neutral-200 bg-white p-8" data-id="element-41" data-mp="1y6f8mqx79jr">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-42" data-mp="3p2m8qwnep0ov">
              <div data-id="element-43" data-mp="3p1b8h6t66b2i">
                <label htmlFor="name" className="mb-2 block text-sm font-semibold text-forest" data-id="element-44" data-mp="3p4qms7f0rg2v">
                  {t.contact.nameLabel}
                </label>
                <input id="name" name="name" type="text" required autoComplete="name" value={form.name} onChange={e => update('name', e.target.value)} placeholder={t.contact.namePlaceholder} className={inputClass} data-id="element-45" data-mp="2dl4ecnrptdf0" />
              </div>
              <div data-id="element-46" data-mp="1mfpc5v4pke7u">
                <label htmlFor="phone" className="mb-2 block text-sm font-semibold text-forest" data-id="element-47" data-mp="1mf77qb7w6tou">
                  {t.contact.phoneFieldLabel}
                </label>
                <input id="phone" name="phone" type="tel" required autoComplete="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder={t.contact.phonePlaceholder} className={inputClass} data-id="element-48" data-mp="1w6k0d51ilnvx" />
              </div>
            </div>

            <div className="mt-5" data-id="element-49" data-mp="2yqt7v81cpwil">
              <label htmlFor="service" className="mb-2 block text-sm font-semibold text-forest" data-id="element-50" data-mp="33r72nodxz9yo">
                {t.contact.serviceLabel}
              </label>
              <select id="service" name="service" value={form.service} onChange={e => update('service', e.target.value)} className={inputClass} data-id="element-51" data-mp="3ckagvywevqud">
                <option value="" data-id="element-52" data-mp="2kt4jvrbzm99b">{t.contact.servicePlaceholder}</option>
                {t.contact.serviceOptions.map(option => <option key={option} value={option} data-id="element-53" data-mp="2ttaxrebllb90">
                    {option}
                  </option>)}
              </select>
            </div>

            <div className="mt-5" data-id="element-54" data-mp="46biyrpvpve8">
              <label htmlFor="message" className="mb-2 block text-sm font-semibold text-forest" data-id="element-55" data-mp="92sjymi6g48q">
                {t.contact.messageLabel}
              </label>
              <textarea id="message" name="message" rows={5} value={form.message} onChange={e => update('message', e.target.value)} placeholder={t.contact.messagePlaceholder} className={`${inputClass} resize-y`} data-id="element-56" data-mp="e7nqqcmrvwm3" />
            </div>

            <button type="submit" disabled={status === 'submitting'} className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-md bg-rust px-6 py-4 font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:bg-rust-dark disabled:cursor-not-allowed disabled:opacity-70" data-id="element-57" data-mp="eehcbytrb3k">
              {status === 'submitting' && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-58" data-mp="1gv0uo378sagy" />}
              {status === 'submitting' ? t.contact.submitting : t.contact.submit}
            </button>

            <div aria-live="polite" className="min-h-[1.5rem]" data-id="element-59" data-mp="24547oru1c8vo">
              {status === 'success' && <p className="mt-4 flex items-start gap-2 text-sm font-semibold text-forest" data-id="element-60" data-mp="1qe7jreb20fuy">
                  <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-61" data-mp="1vcskdeu7x1jx" />
                  {t.contact.success}
                </p>}
              {status === 'error' && <p className="mt-4 text-sm font-semibold text-rust-dark" data-id="element-62" data-mp="1lfe2rbwk66q1">
                  {t.contact.error}
                </p>}
            </div>
          </form>
        </div>
      </div>
    </section>;
}